import React, { useState } from 'react';
import { X, User, Building2, Mail, Phone, MapPin, StickyNote, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AgentCustomerCreateModal = ({ open, onClose, onCreated, agentToken, initialCustomer = null }) => {
  const isEdit = !!initialCustomer;
  const buildInitial = () => {
    if (!initialCustomer) {
      return {
        firstName: '', lastName: '', email: '', phone: '',
        userType: 'business', companyName: '', vatNumber: '', fiscalCode: '',
        sdi: '', pec: '',
        city: '', province: '', address: '', zipCode: '',
        shippingAddress: '', shippingCity: '', shippingProvince: '', shippingZipCode: '', shippingNotes: '',
        contactFirstName: '', contactLastName: '', contactPhone: '', contactEmail: '',
        customerStatus: 'lead', notes: '',
      };
    }
    const c = initialCustomer;
    const bi = c.businessInfo || {};
    const si = c.shippingInfo || {};
    const cp = c.contactPerson || {};
    return {
      firstName: c.firstName || '', lastName: c.lastName || '', email: c.email || '', phone: c.phone || '',
      userType: c.userType || 'business',
      companyName: c.companyName || bi.companyName || '',
      vatNumber: bi.vatNumber || '', fiscalCode: bi.fiscalCode || '',
      sdi: bi.sdi || '', pec: bi.pec || '',
      city: c.city || '', province: c.province || '', address: c.address || '', zipCode: c.zipCode || '',
      shippingAddress: si.address || '', shippingCity: si.city || '', shippingProvince: si.province || '',
      shippingZipCode: si.zipCode || '', shippingNotes: si.notes || '',
      contactFirstName: cp.firstName || '', contactLastName: cp.lastName || '',
      contactPhone: cp.phone || '', contactEmail: cp.email || '',
      customerStatus: c.customerStatus || 'lead', notes: '',
    };
  };
  const [form, setForm] = useState(buildInitial());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const url = isEdit
        ? `${API}/api/agent/customers/${initialCustomer.id}`
        : `${API}/api/agent/customers`;
      const method = isEdit ? 'PUT' : 'POST';
      // per l'edit non inviare email (immutabile via PUT) e non serve
      const body = { ...form };
      if (isEdit) delete body.email;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${agentToken}` },
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (res.ok) {
        onCreated?.(j);
        onClose?.();
        if (!isEdit) {
          // reset solo in modalità creazione
          setForm({ firstName: '', lastName: '', email: '', phone: '', userType: 'business', companyName: '', vatNumber: '', fiscalCode: '', sdi: '', pec: '', city: '', province: '', address: '', zipCode: '', shippingAddress: '', shippingCity: '', shippingProvince: '', shippingZipCode: '', shippingNotes: '', contactFirstName: '', contactLastName: '', contactPhone: '', contactEmail: '', customerStatus: 'lead', notes: '' });
        }
      } else {
        setError(j.detail || 'Errore salvataggio cliente');
      }
    } catch (e) {
      setError(String(e.message));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="agent-new-customer-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-semibold">{isEdit ? 'Modifica cliente' : 'Nuovo cliente'}</h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Tipo */}
          <div className="flex gap-2">
            {['business', 'private'].map((t) => (
              <button key={t} type="button" onClick={() => update('userType', t)}
                data-testid={`type-${t}`}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${
                  form.userType === t ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300'
                }`}>
                {t === 'business' ? <Building2 className="inline w-4 h-4 mr-1" /> : <User className="inline w-4 h-4 mr-1" />}
                {t === 'business' ? 'Azienda' : 'Privato'}
              </button>
            ))}
          </div>

          {form.userType === 'business' && (
            <div className="space-y-3 border border-purple-100 bg-purple-50/30 rounded-lg p-4">
              <div className="text-xs font-bold text-purple-700 uppercase">Dati fatturazione</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase">Ragione sociale *</label>
                  <input required={form.userType === 'business'} value={form.companyName} onChange={(e) => update('companyName', e.target.value)}
                    data-testid="input-company"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase">P.IVA</label>
                  <input value={form.vatNumber} onChange={(e) => update('vatNumber', e.target.value)}
                    data-testid="input-vat"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase">Codice fiscale</label>
                  <input value={form.fiscalCode} onChange={(e) => update('fiscalCode', e.target.value)}
                    data-testid="input-fiscal-code"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase">Codice SDI</label>
                  <input value={form.sdi} onChange={(e) => update('sdi', e.target.value.toUpperCase())}
                    maxLength="7" placeholder="es. M5UXCR1"
                    data-testid="input-sdi"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-gray-700 uppercase">PEC</label>
                  <input type="email" value={form.pec} onChange={(e) => update('pec', e.target.value)}
                    placeholder="fatture@pec.azienda.it"
                    data-testid="input-pec"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase">Nome *</label>
              <input required value={form.firstName} onChange={(e) => update('firstName', e.target.value)}
                data-testid="input-firstname"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase">Cognome *</label>
              <input required value={form.lastName} onChange={(e) => update('lastName', e.target.value)}
                data-testid="input-lastname"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase">Email *</label>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                <input required type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                  disabled={isEdit}
                  data-testid="input-email"
                  className={`w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm ${isEdit ? 'bg-gray-100 text-gray-500' : ''}`} />
              </div>
              {isEdit && <div className="text-[10px] text-gray-500 mt-1">Email non modificabile.</div>}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase">Telefono</label>
              <div className="relative mt-1">
                <Phone className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                <input value={form.phone} onChange={(e) => update('phone', e.target.value)}
                  data-testid="input-phone"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-purple-600 font-medium">Sede legale / indirizzo di fatturazione (opzionale)</summary>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="col-span-2">
                <label className="text-xs font-semibold text-gray-700 uppercase">Via</label>
                <div className="relative mt-1">
                  <MapPin className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                  <input value={form.address} onChange={(e) => update('address', e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase">Città</label>
                <input value={form.city} onChange={(e) => update('city', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase">Provincia</label>
                <input value={form.province} onChange={(e) => update('province', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase">CAP</label>
                <input value={form.zipCode} onChange={(e) => update('zipCode', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>
          </details>

          {form.userType === 'business' && (
            <details className="text-sm">
              <summary className="cursor-pointer text-purple-600 font-medium">Indirizzo di spedizione (se diverso)</summary>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-gray-700 uppercase">Via</label>
                  <input value={form.shippingAddress} onChange={(e) => update('shippingAddress', e.target.value)}
                    data-testid="input-shipping-address"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase">Città</label>
                  <input value={form.shippingCity} onChange={(e) => update('shippingCity', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase">Provincia</label>
                  <input value={form.shippingProvince} onChange={(e) => update('shippingProvince', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase">CAP</label>
                  <input value={form.shippingZipCode} onChange={(e) => update('shippingZipCode', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-gray-700 uppercase">Note di spedizione</label>
                  <textarea rows={2} value={form.shippingNotes} onChange={(e) => update('shippingNotes', e.target.value)}
                    placeholder="Orari consegna, cortesie corriere, telefono referente magazzino…"
                    data-testid="input-shipping-notes"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
              </div>
            </details>
          )}

          {form.userType === 'business' && (
            <details className="text-sm" open>
              <summary className="cursor-pointer text-purple-600 font-medium">Persona di riferimento</summary>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase">Nome</label>
                  <input value={form.contactFirstName} onChange={(e) => update('contactFirstName', e.target.value)}
                    data-testid="input-contact-firstname"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase">Cognome</label>
                  <input value={form.contactLastName} onChange={(e) => update('contactLastName', e.target.value)}
                    data-testid="input-contact-lastname"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase">Cellulare</label>
                  <div className="relative mt-1">
                    <Phone className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                    <input value={form.contactPhone} onChange={(e) => update('contactPhone', e.target.value)}
                      data-testid="input-contact-phone"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 uppercase">Email</label>
                  <div className="relative mt-1">
                    <Mail className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                    <input type="email" value={form.contactEmail} onChange={(e) => update('contactEmail', e.target.value)}
                      data-testid="input-contact-email"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm" />
                  </div>
                </div>
              </div>
            </details>
          )}

          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">Stato iniziale</label>
            <select value={form.customerStatus} onChange={(e) => update('customerStatus', e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
              <option value="lead">Lead</option>
              <option value="prospect">Prospect</option>
              <option value="customer">Cliente</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">Prima nota (opzionale)</label>
            <div className="relative mt-1">
              <StickyNote className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
              <textarea rows={3} value={form.notes} onChange={(e) => update('notes', e.target.value)}
                data-testid="input-notes"
                placeholder="Contesto contatto, esigenze, richieste…"
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md text-sm">Annulla</button>
          <button type="submit" disabled={loading} data-testid="submit-customer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Salva modifiche' : 'Crea cliente'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AgentCustomerCreateModal;
